import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  ListItemButton,
  Collapse,
  Chip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Quiz as QuizIcon,
  Book as BookIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  EventAvailable as EventAvailableIcon,
  Group as GroupIcon
} from '@mui/icons-material';

// Shared drawer width (consistent with teacher menu)
export const DRAWER_WIDTH = 280;
export const DRAWER_WIDTH_COLLAPSED = 72;
const drawerWidth = DRAWER_WIDTH;
const collapsedDrawerWidth = DRAWER_WIDTH_COLLAPSED;

const StudentNavigation = ({ 
  open, 
  onClose, 
  collapsed = false, 
  onToggleCollapse,
  restricted = false,
  supportOpen,
  onSupportOpen 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Navigation menu items with grouping
  const getMenuItems = () => {
    return [
      {
        group: 'Main',
        items: [
          { text: 'Home', icon: <DashboardIcon />, path: '/dashboard', ariaLabel: 'Navigate to Home' }
        ]
      },
      {
        group: 'Learning',
        items: [
          { text: 'My Exam Plan', icon: <EventAvailableIcon />, path: '/student/exam-prep', ariaLabel: 'My Exam Plan' },
          { text: 'My Assignments', icon: <QuizIcon />, path: '/tests', ariaLabel: 'My Assignments' },
          { text: 'My Revision Notes', icon: <BookIcon />, path: '/revision-notes', ariaLabel: 'My Revision Notes' },
          { text: 'My Progress', icon: <TrendingUpIcon />, path: '/analytics/student', ariaLabel: 'My Progress' },
          { text: 'My Teachers & Parents', icon: <GroupIcon />, path: '/profile', state: { activeMenu: 'teachers' }, ariaLabel: 'My Teachers and Parents' }
        ]
      }
    ];
  };

  const menuItems = getMenuItems();

  const isActive = (path) => {
    return location.pathname === path;
  };

  const renderMenuItem = (item) => {
    const active = isActive(item.path);
    const itemDisabled = restricted && item.path !== '/dashboard';
    
    const menuItem = (
      <ListItemButton
        key={item.text}
        onClick={() => {
          if (!itemDisabled) {
            navigate(item.path, item.state ? { state: item.state } : undefined);
          }
        }}
        selected={active}
        disabled={itemDisabled}
        sx={{
          borderRadius: 1,
          mx: 1,
          mb: 0.5,
          minHeight: 48,
          position: 'relative',
          color: 'rgba(255, 255, 255, 0.7)',
          '&.Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.16)',
            color: '#FFFFFF',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.24)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 4,
              backgroundColor: '#1976d2',
              borderRadius: '0 2px 2px 0'
            }
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            color: '#FFFFFF',
          },
          '&.Mui-disabled': {
            opacity: 0.45,
            color: 'rgba(255, 255, 255, 0.45)'
          }
        }}
        aria-label={item.ariaLabel}
      >
        <ListItemIcon 
          sx={{ 
            minWidth: collapsed ? 40 : 48,
            color: active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'
          }}
        >
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <ListItemText 
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: active ? 600 : 400,
                color: active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'
              }}
            />
            {itemDisabled ? (
              <Chip
                label="Locked"
                size="small"
                sx={{
                  height: 22,
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  color: 'rgba(255, 255, 255, 0.78)',
                  fontWeight: 600
                }}
              />
            ) : null}
          </Box>
        )}
      </ListItemButton>
    );

    // Add tooltip when collapsed
    if (collapsed) {
      return (
        <Tooltip 
          key={item.text}
          title={item.text} 
          placement="right"
          arrow
        >
          {menuItem}
        </Tooltip>
      );
    }

    return menuItem;
  };

  const renderMenuGroup = (group) => {
    if (collapsed) {
      return group.items.map(renderMenuItem);
    }

    return (
      <Box key={group.group}>
        <Typography
          variant="caption"
          sx={{
            px: 3,
            py: 1,
            color: 'rgba(255, 255, 255, 0.5)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: '0.75rem'
          }}
        >
          {group.group}
        </Typography>
        {group.items.map(renderMenuItem)}
      </Box>
    );
  };

  const drawerContent = (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      overflow: 'hidden'
    }}>
      {/* Header: on mobile show back arrow to close; on desktop show collapse */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        px: 2,
        py: 1.5,
        minHeight: 56,
        borderBottom: '1px solid',
        borderColor: 'rgba(255, 255, 255, 0.12)'
      }}>
        {!collapsed && (
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700,
              color: '#FFFFFF',
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            ThinkStyne
          </Typography>
        )}
        <IconButton 
          onClick={isMobile ? onClose : onToggleCollapse}
          size="medium"
          sx={{ 
            ml: collapsed ? 0 : 'auto',
            color: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              color: '#FFFFFF',
              backgroundColor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
          aria-label={isMobile ? 'Close menu' : (collapsed ? 'Expand sidebar' : 'Collapse sidebar')}
        >
          {isMobile ? <ArrowBackIcon /> : (collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />)}
        </IconButton>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        py: 1
      }}>
        <List sx={{ py: 0 }}>
          {menuItems.map(renderMenuGroup)}
        </List>
      </Box>

    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? open : true}
      onClose={onClose}
      sx={{
        width: collapsed ? collapsedDrawerWidth : drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: collapsed ? collapsedDrawerWidth : drawerWidth,
          boxSizing: 'border-box',
          border: 'none',
          boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
          backgroundColor: '#1a237e',
          color: '#FFFFFF',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflow: 'hidden'
        },
      }}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile.
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default StudentNavigation; 
